import './styles.scss';
import { AppContext } from '../../context';
import {
    AvailableInterviewOptions,
    InterviewOrigin,
    InterviewStatus,
    InterviewerAlreadyBookedError,
    MultiDayOption,
    MultiDayOptionGroup,
    SingleDayOption,
} from '../../types/index';
import { Button } from '@goodtimeio/rain';
import {
    DISABLED_SUGGEST_TIME_SEARCH_TIME,
    ENABLED_SUGGEST_TIME_SEARCH_TIME,
    MAX_NUMBER_OF_SUGGESTED_TIME_CARDS,
    MAX_SEARCH_TIME_IN_MILLISECONDS,
} from '../../utils/constants';
import { DateTime, Interval } from 'luxon';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Redirect, useHistory, useParams } from 'react-router-dom';
import { faSpinner } from '@fortawesome/pro-light-svg-icons';
import { getDateShort, getTimezone } from '../../utils/date-utils';
import { isMobile } from 'react-device-detect';
import { stopAvailabilitySearch } from '../../api';
import { timeZoneList } from '../../utils/timezones';
import DatePicker from '../../components/DatePicker/DatePicker';
import InterviewOptionsPicker from '../../components/InterviewOptions/InterviewOptionPicker';
import ListTimezones from '../../components/ListTimezones';
import React, { ReactElement, useContext, useEffect, useRef, useState } from 'react';
import SpinnerLoading from '../../components/SpinnerLoading';
import Wrapper from '../../components/Wrapper';

import { createArrayOfSelectedTimes, createListOfDays } from './suggestTimes';

const PickDays = () => {
    const {
        meetIntro,
        selectedDays,
        selectedTimeSlots = [],
        addTimeSlot,
        addTimeSlots,
        addDays,
        addTimezone,
        availableInterviewOptions,
        lastResultReceivedTime,
        postConfirmCandidateAvailability,
        storeAndSendUserAction,
        postCandidateReschedule,
        translate,
        selectedLanguage,
        searchMetadata,
    } = useContext(AppContext);
    const { hash } = useParams<{ hash: string }>();
    const maxLoadingSpinnerTime = meetIntro?.data?.interview?.settings?.allowGuestToSuggestTimes
        ? ENABLED_SUGGEST_TIME_SEARCH_TIME
        : MAX_SEARCH_TIME_IN_MILLISECONDS;
    const interviewIsPendingCandidateResponse =
        meetIntro &&
        !meetIntro?.isLoading &&
        meetIntro?.data?.interview.status === InterviewStatus.PENDING_CANDIDATE_RESPONSE;
    const allowToSuggestTimesFeatureFlag = meetIntro?.data?.organization?.settings?.allowToSuggestTimesFeatureFlag;
    const listOfSuggestedDays = createListOfDays(meetIntro?.data?.interview);
    const suggestedFirstDates = meetIntro?.data?.searchRanges;
    const logo = meetIntro?.data?.organization?.logoUrl;
    const primaryColor = meetIntro?.data?.meetCompanyColor ? meetIntro?.data?.meetCompanyColor : '#6400E0';
    let textColor = '#FFFFFF';
    if (meetIntro?.data?.meetFontColor) {
        textColor = meetIntro?.data?.meetFontColor;
    }
    const history = useHistory();

    const ref = useRef<HTMLDivElement>(null);

    const [availableOptions, setAvailableOptions] = useState<AvailableInterviewOptions>({
        singleDay: new Map<string, SingleDayOption[]>(),
        multiDay: new Map<number, MultiDayOption[]>(),
        timezone: '',
    });

    const [activeAvailableOptions, setActiveAvailableOptions] = useState<AvailableInterviewOptions>({
        singleDay: new Map<string, SingleDayOption[]>(),
        multiDay: new Map<number, MultiDayOption[]>(),
        timezone: '',
    });

    const [isMultiDayChecked, setIsMultiDayChecked] = useState<boolean>(true);
    const [minSearchTimeAchieved, setMinSearchTimeAchieved] = useState<boolean>(false);
    const [maxSearchTimeAchieved, setMaxSearchTimeAchieved] = useState<boolean>(false);
    const [maxLoadingSpinnerTimeAchieved, setMaxLoadingSpinnerTimeAchieved] = useState<boolean>(false);
    const [firstResultHasArrived, setFirstResultHasArrived] = useState<boolean>(false);
    const [loadingMessage, setLoadingMessage] = useState<string>(
        translate('intro:calculatingAndBuildingInterviewSchedules'),
    );
    const [areSearchRangesAvailable, setAreSearchRangesAvailable] = useState<boolean>(false);

    const [startX, setStartX] = useState<number>(0);
    const [scrollLeft, setScrollLeft] = useState<number>(0);
    const [selectedTimezone, setSelectedTimezone] = useState<string>(getTimezone(meetIntro?.data?.interview));
    const [isScheduling, setIsScheduling] = useState<boolean>(false);

    const isDownRef = useRef<boolean>(false);

    const setIsDownState = (isDown: boolean) => {
        isDownRef.current = isDown;
    };

    useEffect(() => {
        if (suggestedFirstDates?.length && !areSearchRangesAvailable) {
            setAreSearchRangesAvailable(true);
        }
    }, [suggestedFirstDates, areSearchRangesAvailable]);

    useEffect(() => {
        if (
            !firstResultHasArrived &&
            (availableInterviewOptions.multiDay.size > 0 || availableInterviewOptions.singleDay.size > 0)
        ) {
            setFirstResultHasArrived(true);
        }
    }, [availableInterviewOptions, firstResultHasArrived]);

    useEffect(() => {
        if (searchMetadata && !searchMetadata.isLoading) {
            const setMaxLoadingSpinnerTimeAchievedTimeout = setTimeout(() => {
                setMaxLoadingSpinnerTimeAchieved(true);
            }, maxLoadingSpinnerTime);

            return () => {
                clearTimeout(setMaxLoadingSpinnerTimeAchievedTimeout);
            };
        }
    }, [searchMetadata, maxLoadingSpinnerTime]);

    useEffect(() => {
        const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
        if (timezone) {
            if (
                !meetIntro?.data?.interview.settings.isOnsite &&
                meetIntro?.data?.guest.settings.timeZone !== timezone
            ) {
                addTimezone(timezone);
            }
        }
        //ToDo: Change this to avoid the warning.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (!minSearchTimeAchieved && searchMetadata && !searchMetadata.isLoading) {
            const setMinSearchTimeAchievedTimeout = setTimeout(() => {
                setLoadingMessage(translate('intro:takingLongThanExpected'));
                setMinSearchTimeAchieved(true);
            }, DISABLED_SUGGEST_TIME_SEARCH_TIME);

            return () => {
                clearTimeout(setMinSearchTimeAchievedTimeout);
            };
        }
    }, [minSearchTimeAchieved, searchMetadata, translate]);

    useEffect(() => {
        if (searchMetadata && !searchMetadata.isLoading) {
            const setMaxSearchTimeAchievedTimeout = setTimeout(() => {
                setMaxSearchTimeAchieved(true);
                stopAvailabilitySearch('timeout');
            }, MAX_SEARCH_TIME_IN_MILLISECONDS);

            return () => {
                clearTimeout(setMaxSearchTimeAchievedTimeout);
            };
        }
    }, [lastResultReceivedTime, searchMetadata]);

    const handleSelectTime = (newSelectedTimeId: string) => {
        addTimeSlot(newSelectedTimeId);
        if (selectedTimeSlots.length <= 1) {
            storeAndSendUserAction(hash, 'pickedFirstResult');
        }
    };

    const onSelectDays = (selectedDays: Set<string>) => {
        addDays(Array.from(selectedDays));
    };

    const handleSelectTimeCategory = (day: string, timeCategory: string) => {
        const optionsByDay = availableOptions.singleDay.get(day);

        const result =
            optionsByDay
                ?.filter((currTime) => currTime.timeCategory.label === timeCategory)
                .map((currTime) => currTime.hash) || [];

        addTimeSlots(result);
    };

    const onSelectTimezone = (timezone: string) => {
        setSelectedTimezone(timezone);
        addTimezone(timezone);
    };

    const getActiveMultiDayInterviewOptions = (
        multiDayOptionGroup: MultiDayOptionGroup,
        multiDay: MultiDayOption,
        numberOfDays: number,
        newSelectedDays: string[],
    ) => {
        let shouldRemoveMultiDayFromSelected = true;
        if (multiDay.duration.length === numberOfDays) {
            const matches = multiDay.duration.every((duration) => {
                return (
                    newSelectedDays.includes(getDateShort(DateTime.fromISO(duration.startTime), 'en')) ||
                    newSelectedDays.includes(getDateShort(DateTime.fromISO(duration.endTime), 'en'))
                );
            });
            if (matches) {
                const existingOptions = multiDayOptionGroup.get(numberOfDays);
                if (existingOptions) {
                    existingOptions.push(multiDay);
                } else {
                    multiDayOptionGroup.set(numberOfDays, [multiDay]);
                }
                shouldRemoveMultiDayFromSelected = false;
            }
        }
        if (shouldRemoveMultiDayFromSelected && selectedTimeSlots.includes(multiDay.hash)) {
            addTimeSlot(multiDay.hash);
        }
    };

    const getActiveInterviewOptions = (newSelectedDays: string[]) => {
        const newActiveAvailableOptions: AvailableInterviewOptions = {
            singleDay: new Map<string, SingleDayOption[]>(),
            multiDay: new Map<number, MultiDayOption[]>(),
            timezone: selectedTimezone,
        };

        availableOptions.singleDay.forEach((singleDayOption, date: string) => {
            if (newSelectedDays.includes(date)) {
                newActiveAvailableOptions.singleDay.set(date, singleDayOption);
            } else if (selectedTimeSlots?.length) {
                const selectedTimeSlot = singleDayOption.find((singleDay) =>
                    selectedTimeSlots.includes(singleDay.hash),
                );
                if (selectedTimeSlot) {
                    addTimeSlot(selectedTimeSlot.hash);
                }
            }
        });

        const unsortedArray = [...newActiveAvailableOptions.singleDay];
        unsortedArray.sort(([key1], [key2]) => {
            const startTimeA = DateTime.fromJSDate(new Date(key1));
            const startTimeB = DateTime.fromJSDate(new Date(key2));
            if (startTimeA > startTimeB) {
                return 1;
            }
            return -1;
        });
        newActiveAvailableOptions.singleDay = new Map<string, SingleDayOption[]>(unsortedArray);

        availableOptions.multiDay.forEach((multiDayOption, numberOfDays) => {
            multiDayOption.forEach((multiDay) => {
                getActiveMultiDayInterviewOptions(
                    newActiveAvailableOptions.multiDay,
                    multiDay,
                    numberOfDays,
                    newSelectedDays,
                );
            });
        });
        const unsortedMultiDayArray = [...newActiveAvailableOptions.multiDay];
        unsortedMultiDayArray.sort(([key1], [key2]) => key1 - key2);
        newActiveAvailableOptions.multiDay = new Map<number, MultiDayOption[]>(unsortedMultiDayArray);
        return newActiveAvailableOptions;
    };

    const hasTheMeetingStarted = () => {
        const interviewStartTime = meetIntro?.data?.interview?.startTime;
        return interviewStartTime && new Date(interviewStartTime).getTime() - new Date().getTime() < 0;
    };

    const onSchedule = async () => {
        setIsScheduling(true);
        stopAvailabilitySearch('confirm');
        try {
            if (meetIntro?.data?.interview.status === InterviewStatus.RESCHEDULE) {
                if (hasTheMeetingStarted()) {
                    history.push(`/confirm/${hash}`);
                    return document.location.reload();
                }
                await postCandidateReschedule(hash);
                setIsScheduling(false);
                return history.push(`/confirm/${hash}`);
            }

            await postConfirmCandidateAvailability(hash);
            storeAndSendUserAction(hash, 'confirmAvailability');
            setIsScheduling(false);
            history.push(`/confirm/${hash}`);
        } catch (e) {
            if (e instanceof InterviewerAlreadyBookedError) {
                setIsScheduling(false);
                history.push(`/time-already-booked/${hash}`);
            }
        }
    };

    const displaySuggestTimesSlots = (date: string) => {
        const tz = selectedTimezone;
        const timeRangesWithSlots = createArrayOfSelectedTimes(meetIntro?.data?.interview, tz);
        const singleDaysAvOpt = Array.from(availableInterviewOptions.singleDay);
        const [, singleDay = []] = singleDaysAvOpt.find((val) => val?.[0] === date) || [];
        const [, suggestedTimesSingleDay = []] = timeRangesWithSlots?.find((val) => val?.[0] === date) ?? [];
        const singleDayEventsToOverlap = singleDay.map((val) => {
            return Interval.fromDateTimes(
                DateTime.fromISO(val.duration.startTime, {
                    setZone: false,
                    zone: tz,
                }),
                DateTime.fromISO(val.duration.endTime, {
                    setZone: false,
                    zone: tz,
                }),
            );
        });

        const cleanTimeSlots = [...suggestedTimesSingleDay].filter((val) => {
            const interval = Interval.fromDateTimes(
                DateTime.fromISO(val.duration.startTime, { zone: tz }),
                DateTime.fromISO(val.duration.endTime, { zone: tz }),
            );

            return !singleDayEventsToOverlap.some((val) => interval.overlaps(val));
        });

        const sortedSingleDays = cleanTimeSlots.concat(singleDay).sort((key1, key2) => {
            const startTimeA = DateTime.fromJSDate(new Date(key1.duration.startTime));
            const startTimeB = DateTime.fromJSDate(new Date(key2.duration.startTime));
            if (startTimeA > startTimeB) {
                return 1;
            }
            return -1;
        });
        availableOptions.singleDay.set(date, sortedSingleDays);
        availableInterviewOptions.singleDay.set(date, sortedSingleDays);

        setActiveAvailableOptions(getActiveInterviewOptions(selectedDays || []));
    };

    const getSingleDayPickers = () => {
        const singleDayPickers: ReactElement[] = [];
        activeAvailableOptions.singleDay.forEach((singleDayOptions, date) => {
            singleDayPickers.push(
                <InterviewOptionsPicker
                    singleDayInterviewOptions={singleDayOptions}
                    companyColor={primaryColor}
                    textColor={textColor}
                    selectedTimeIds={selectedTimeSlots}
                    setSelectedTimeIds={handleSelectTime}
                    setSelectedTimeCategory={handleSelectTimeCategory}
                    date={date}
                    timezone={selectedTimezone}
                    displaySuggestTimesSlots={displaySuggestTimesSlots}
                    allowGuestToSuggestTimes={meetIntro?.data?.interview?.settings?.allowGuestToSuggestTimes}
                    allowToSuggestTimesFeatureFlag={allowToSuggestTimesFeatureFlag}
                    isAlgoLoading={maxSearchTimeAchieved}
                />,
            );
        });
        return singleDayPickers;
    };

    const getMultiDayPickers = () => {
        const multiDayPickers: ReactElement[] = [];
        activeAvailableOptions.multiDay.forEach((multiDayOptions, numberOfDays) => {
            multiDayPickers.push(
                <InterviewOptionsPicker
                    companyColor={primaryColor}
                    textColor={textColor}
                    multiDayInterviewOptions={multiDayOptions}
                    selectedTimeIds={selectedTimeSlots}
                    setSelectedTimeIds={handleSelectTime}
                    numberOfDays={numberOfDays}
                    timezone={availableInterviewOptions?.timezone}
                    allowGuestToSuggestTimes={false}
                />,
            );
        });
        return multiDayPickers;
    };

    useEffect(() => {
        if (!allowToSuggestTimesFeatureFlag) {
            return;
        }
        if (meetIntro?.data?.interview?.settings?.allowGuestToSuggestTimes && suggestedFirstDates?.length) {
            if (!selectedDays?.length && maxLoadingSpinnerTimeAchieved && areSearchRangesAvailable) {
                let iterator = 0;
                const suggestedFirstDays = [];
                while (iterator < MAX_NUMBER_OF_SUGGESTED_TIME_CARDS && suggestedFirstDates.length > iterator) {
                    const day = suggestedFirstDates[iterator]?.startTime;
                    if (day !== undefined) {
                        suggestedFirstDays.push(getDateShort(DateTime.fromISO(day), 'en'));
                    }
                    iterator++;
                }
                addDays(suggestedFirstDays);
            }
        }

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [maxLoadingSpinnerTimeAchieved, areSearchRangesAvailable]);

    useEffect(() => {
        if (availableInterviewOptions.singleDay.size || availableInterviewOptions.multiDay.size) {
            const autoLoadDays = getAutoLoadDays(availableInterviewOptions);
            selectedDays?.forEach((day) => autoLoadDays.add(day));
            const autoLoadList = Array.from(autoLoadDays);
            addDays(autoLoadList);
        }

        if (availableInterviewOptions) {
            setAvailableOptions(availableInterviewOptions);
        }

        if (allowToSuggestTimesFeatureFlag) {
            if (meetIntro?.data?.interview?.settings?.allowGuestToSuggestTimes && listOfSuggestedDays.length) {
                setAvailableOptions({
                    ...availableInterviewOptions,
                    singleDay: new Map<string, SingleDayOption[]>(
                        listOfSuggestedDays.map((day) => {
                            if (availableInterviewOptions.singleDay.has(day)) {
                                const val: SingleDayOption[] = availableInterviewOptions.singleDay.get(day) || [];
                                return [day, [...val]];
                            }
                            return [day, []];
                        }),
                    ),
                });
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        availableInterviewOptions,
        activeAvailableOptions.timezone,
        meetIntro?.data?.interview?.settings?.allowGuestToSuggestTimes,
    ]);

    useEffect(() => {
        if (selectedDays?.length) {
            setActiveAvailableOptions(getActiveInterviewOptions(selectedDays));
        } else {
            setActiveAvailableOptions({
                singleDay: new Map<string, SingleDayOption[]>(),
                multiDay: new Map<number, MultiDayOption[]>(),
                timezone: '',
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedDays?.length, isMultiDayChecked, availableOptions]);

    useEffect(() => {
        if (ref.current) {
            const slider = ref.current;
            slider.addEventListener('mousedown', (e) => {
                setIsDownState(true);
                if (slider?.offsetLeft !== undefined) {
                    setStartX(e.pageX - slider.offsetLeft);
                    setScrollLeft(slider.scrollLeft);
                    slider.classList.add('active');
                }
            });
            slider.addEventListener('mouseleave', () => {
                setIsDownState(false);
                slider.classList.remove('active');
            });
            slider.addEventListener('mouseup', () => {
                setIsDownState(false);
                slider.classList.remove('active');
            });

            slider.addEventListener('mousemove', (event) => {
                if (!isDownRef.current) {
                    return;
                }
                if (isDownRef.current) {
                    event.preventDefault();
                    const x = event.pageX - slider.offsetLeft;
                    const walk = (x - startX) * 3;
                    slider.scrollLeft = scrollLeft - walk;
                }
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [ref.current, isDownRef.current]);

    if (meetIntro?.data?.interview?.origin === InterviewOrigin.SUPERDAY) {
        document.location.href = `/s/intro/${hash}`;
        return null;
    }

    const isLoading = !maxLoadingSpinnerTimeAchieved && !firstResultHasArrived;
    const isLoadingResults = !maxSearchTimeAchieved;
    const backgroundImage = meetIntro?.data?.meetBackgroundUrlList[1] || meetIntro?.data?.meetBackgroundUrlList[0];

    const dynamicButtonStyle = !selectedTimeSlots.length
        ? {}
        : { background: primaryColor, border: primaryColor, color: textColor };

    if (meetIntro && !meetIntro.isLoading && meetIntro?.data?.isPastTimeWindow) {
        stopAvailabilitySearch('expired');
        return <Redirect to={`/expired/${hash}`} />;
    }

    if (
        (!meetIntro || !interviewIsPendingCandidateResponse) &&
        !meetIntro?.isLoading &&
        meetIntro?.data?.interview.status !== InterviewStatus.RESCHEDULE &&
        meetIntro?.data?.interview.status !== InterviewStatus.PENDING_REVIEW
    ) {
        return <Redirect to={`/intro/${hash}`} />;
    }

    if (
        !isLoadingResults &&
        !availableOptions?.singleDay?.size &&
        !availableOptions?.multiDay?.size //&&
        //allowToSuggestTimesFeatureFlag &&
        //!meetIntro?.data?.interview?.settings?.allowGuestToSuggestTimes
    ) {
        storeAndSendUserAction(hash, 'noResults');
        return <Redirect to={`/no-result/${hash}`} />;
    }

    const hasRequireReviewOption = meetIntro?.data?.interview?.settings?.needsReview;
    return (
        <Wrapper loading={isLoading} backgroundImage={backgroundImage} logo={logo} message={loadingMessage}>
            <div className="scheduleContainer">
                <SpinnerLoading loading={isLoadingResults}></SpinnerLoading>
                <div className="datePickerContainer">
                    <div className="datePickerContainerHeader">
                        {selectedDays?.length ? (
                            <>
                                <b>{translate('pickDays:excellent')}</b> &nbsp; {translate('pickDays:addMoreDays')}
                            </>
                        ) : (
                            <>
                                <b>{translate('pickDays:letsFindATime')}</b> &nbsp; {translate('pickDays:tapDays')}
                            </>
                        )}
                    </div>
                    <DatePicker
                        selectedOptions={selectedDays}
                        onSelect={onSelectDays}
                        availableOptions={availableOptions}
                        primaryColor={primaryColor}
                        isMultiDayChecked={isMultiDayChecked}
                        handleMultiDayChecked={() => setIsMultiDayChecked(true)}
                        interview={meetIntro?.data?.interview}
                        selectedLanguage={selectedLanguage}
                    />
                </div>
                <div className="titleOnsiteTimezone">
                    {meetIntro.data?.interview.settings.isOnsite ? (
                        <>
                            <b> {translate('pickDays:onSiteInterview')} </b>
                        </>
                    ) : (
                        <></>
                    )}
                </div>
                <div className="listTimezonesContainer">
                    <ListTimezones
                        selectedValue={{
                            value: selectedTimezone,
                            label: '',
                        }}
                        onChange={(val) => onSelectTimezone(val)}
                        options={timeZoneList}
                        isDisabled={meetIntro?.data?.interview?.settings.isOnsite ?? false}
                    />
                </div>
                {selectedDays?.length ? (
                    <>
                        <div className="optionPickerContainer" ref={ref}>
                            <>
                                {getSingleDayPickers().map((element, index) => (
                                    <React.Fragment key={`single-day-interview-option-picker-${index}`}>
                                        {element}
                                    </React.Fragment>
                                ))}
                                {getMultiDayPickers().map((element, index) => (
                                    <React.Fragment key={`multi-day-interview-option-picker-${index}`}>
                                        {element}
                                    </React.Fragment>
                                ))}
                            </>
                        </div>
                        {selectedTimeSlots.length > 0 && !isMobile ? (
                            <div className="optionPickerContainerFooter">
                                <b>{translate('pickDays:lookingGood')}&nbsp;</b>
                                {translate('pickDays:forGreaterFlexibility')}
                            </div>
                        ) : null}

                        <div className="footerContainer">
                            <Button
                                disabled={!selectedTimeSlots.length || isScheduling}
                                onClick={onSchedule}
                                className={'scheduleBtn'}
                                style={dynamicButtonStyle}
                                hidden={isMobile && !selectedTimeSlots.length}
                            >
                                {isScheduling ? (
                                    <FontAwesomeIcon icon={faSpinner} spin />
                                ) : (
                                    translate(
                                        hasRequireReviewOption
                                            ? 'pickDays:submitAvailability'
                                            : 'pickDays:scheduleInterview',
                                    )
                                )}
                            </Button>
                        </div>
                    </>
                ) : null}
            </div>
        </Wrapper>
    );
};

const getAutoLoadDays = (availableInterviewOptions: AvailableInterviewOptions) => {
    const daysForAutoLoad = new Set<string>(Array.from(availableInterviewOptions?.singleDay)?.map(([day]) => day));
    availableInterviewOptions?.multiDay?.forEach((multiDayOptions) => {
        multiDayOptions.forEach((multiDayOption) => {
            multiDayOption.duration.forEach((duration) => {
                const startTime = DateTime.fromISO(duration.startTime, { setZone: true }).toLocaleString(
                    DateTime.DATE_SHORT,
                );
                const endTime = DateTime.fromISO(duration.endTime, { setZone: true }).toLocaleString(
                    DateTime.DATE_SHORT,
                );
                daysForAutoLoad.add(startTime);
                daysForAutoLoad.add(endTime);
            });
        });
    });
    return daysForAutoLoad;
};

export default PickDays;
